import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, LinkIcon, Search } from "lucide-react";
import { toast } from "sonner";
import PdfReportMenu from "@/components/PdfReportMenu";

export default function ProfessionalPanel() {
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [linkEmail, setLinkEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/pro/patients").then((r) => setPatients(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const link = async () => {
    if (!linkEmail) return;
    try {
      await api.post("/pro/link-patient", { patient_email: linkEmail });
      toast.success("Paciente vinculado");
      setLinkEmail("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al vincular");
    }
  };

  const filtered = patients.filter((p) =>
    (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
    (p.email || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="professional-panel">
      <div>
        <p className="text-sm uppercase tracking-widest text-primary font-semibold">Panel profesional</p>
        <h1 className="heading text-4xl font-extrabold mt-1">Pacientes vinculados</h1>
        <p className="text-lg text-foreground/70 mt-2">Gestiona tus casos, revisa perfiles y exporta informes.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="rounded-2xl border-border md:col-span-2">
          <CardHeader>
            <CardTitle className="heading flex items-center gap-2"><Users size={18} /> Vincular paciente existente</CardTitle>
            <CardDescription>Introduce el email del paciente para asignarlo a tu cartera.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Input placeholder="paciente@correo.com" className="h-11 flex-1 min-w-64"
              value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} data-testid="link-patient-input" />
            <Button onClick={link} className="rounded-full h-11" data-testid="link-patient-btn">
              <LinkIcon size={16} className="mr-1" /> Vincular
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border">
          <CardHeader>
            <CardTitle className="heading">Resumen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl heading font-extrabold text-primary">{patients.length}</div>
            <div className="text-sm text-muted-foreground">pacientes en tu cartera</div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-border">
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="heading">Listado</CardTitle>
            <CardDescription>Pincha en un paciente para ver evolución y exportar.</CardDescription>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="pl-9 h-10 w-64" data-testid="search-patients" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p>Cargando...</p> :
            filtered.length === 0 ? (
              <p className="text-muted-foreground py-4">Todavía no hay pacientes vinculados.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="py-3 pr-3">Nombre</th>
                      <th className="py-3 pr-3">Correo</th>
                      <th className="py-3 pr-3">Intentos</th>
                      <th className="py-3 pr-3">PRE</th>
                      <th className="py-3 pr-3">POST</th>
                      <th className="py-3 pr-3">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id} className="border-b border-border/60" data-testid={`patient-row-${p.id}`}>
                        <td className="py-3 pr-3 font-medium">{p.name}</td>
                        <td className="py-3 pr-3 text-muted-foreground">{p.email}</td>
                        <td className="py-3 pr-3">
                          <Badge variant="secondary">{p.attempts_count}</Badge>
                        </td>
                        <td className="py-3 pr-3">{p.pre_accuracy != null ? `${Math.round(p.pre_accuracy * 100)}%` : "—"}</td>
                        <td className="py-3 pr-3">{p.post_accuracy != null ? `${Math.round(p.post_accuracy * 100)}%` : "—"}</td>
                        <td className="py-3 pr-3">
                          <div className="flex gap-2">
                            <Link to={`/pro/patients/${p.id}`}>
                              <Button size="sm" variant="outline" className="rounded-full" data-testid={`view-patient-${p.id}`}>Ver</Button>
                            </Link>
                            <PdfReportMenu
                              patientId={p.id}
                              patientEmail={p.email}
                              label="PDF"
                              variant="ghost"
                              size="sm"
                              testIdPrefix={`pdf-patient-${p.id}`}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </CardContent>
      </Card>
    </div>
  );
}
